import { KDF_ITERATIONS } from './cryptography';
import { committed, readAll, toPromise } from './idb';
import { hasLegacyVault, migrations, verifyLegacyPassword } from './migrations';
import { ensureMigrationStore, pendingMigrations, runDataPhase, runSchemaPhase } from './migrator';
import { REQUIRED_STORES, STORE_MESSAGES, STORE_META } from './stores';
export class VaultUnavailableError extends Error {
}
export class VaultBlockedError extends Error {
}
export class VaultStaleError extends Error {
}
const META_ID = 'vault';
function messageRowKey(message) {
    return `${message.sender}:${message.id}`;
}
function chatRange(chatId) {
    return IDBKeyRange.bound([chatId], [chatId, []]);
}
export default function getDatabase(logger, cryptography) {
    let initialName;
    let db = null;
    let openPromise = null;
    let dataKey = null;
    let stale = false;
    const messageIndexes = new Map();
    function request(name, version) {
        return new Promise((resolve, reject) => {
            let settled = false;
            const open = version === undefined ? indexedDB.open(name) : indexedDB.open(name, version);
            open.onupgradeneeded = () => {
                const upgraded = open.result;
                ensureMigrationStore(upgraded);
                runSchemaPhase(upgraded, open.transaction, migrations, logger);
            };
            open.onblocked = () => {
                if (settled)
                    return;
                settled = true;
                logger.warn('[database] indexedDB upgrade is blocked by another tab.');
                reject(new VaultBlockedError('Another tab is holding an older version of the vault open.'));
            };
            open.onsuccess = () => {
                if (settled) {
                    open.result.close();
                    return;
                }
                settled = true;
                resolve(open.result);
            };
            open.onerror = () => {
                if (settled)
                    return;
                settled = true;
                reject(open.error);
            };
        });
    }
    async function connect() {
        const name = initialName;
        if (name === undefined || name === null) {
            const error = '[database] indexedDB has not been initialized yet.';
            logger.error(error);
            throw new Error(error);
        }
        if (typeof indexedDB === 'undefined') {
            throw new VaultUnavailableError('This browser does not expose indexedDB.');
        }
        let opened;
        try {
            opened = stale ? await request(name) : await request(name, +process.env.EPHEMON_BUILD_TIMESTAMP);
        }
        catch (error) {
            if (error instanceof VaultBlockedError)
                throw error;
            if (error?.name === 'VersionError') {
                stale = true;
                opened = await request(name);
            }
            else {
                throw new VaultUnavailableError(`indexedDB could not be opened: ${error?.message}`);
            }
        }
        const missing = REQUIRED_STORES.filter((store) => !opened.objectStoreNames.contains(store));
        if (missing.length > 0) {
            opened.close();
            throw new VaultStaleError(`This tab is older than the stored vault (missing ${missing.join(', ')}).`);
        }
        return attach(opened);
    }
    function attach(opened) {
        opened.onversionchange = () => {
            logger.warn('[database] Another tab is upgrading the vault; closing this connection.');
            stale = true;
            opened.close();
            if (db === opened) {
                db = null;
                openPromise = null;
            }
        };
        opened.onclose = () => {
            if (db === opened) {
                db = null;
                openPromise = null;
            }
        };
        db = opened;
        return opened;
    }
    async function reopenAtNextVersion(current) {
        const name = initialName;
        const next = current.version + 1;
        current.close();
        db = null;
        openPromise = null;
        return attach(await request(name, next));
    }
    async function drainMigrations(current, password, handlers) {
        for (let round = 0; round <= migrations.length; round++) {
            const before = await pendingMigrations(current, migrations);
            if (before.length === 0)
                return;
            await runDataPhase({
                db: current,
                cryptography,
                logger,
                dataKey: requireKey(),
                password,
                report: (done, total) => handlers?.onProgress?.(done, total),
            }, migrations);
            const after = await pendingMigrations(current, migrations);
            if (after.length === 0)
                return;
            if (after.length === before.length) {
                throw new Error('[database] Migrations stopped making progress.');
            }
            current = await reopenAtNextVersion(current);
        }
        throw new Error('[database] Migrations did not converge.');
    }
    function open() {
        if (db !== null)
            return Promise.resolve(db);
        if (openPromise !== null)
            return openPromise;
        openPromise = connect().catch((error) => {
            openPromise = null;
            throw error;
        });
        return openPromise;
    }
    function requireKey() {
        if (dataKey === null) {
            const error = '[database] The vault is locked.';
            logger.error(error);
            throw new Error(error);
        }
        return dataKey;
    }
    async function readMeta(database) {
        const transaction = database.transaction([STORE_META], 'readonly');
        const record = await toPromise(transaction.objectStore(STORE_META).get(META_ID));
        return record ?? null;
    }
    async function writeMeta(database, record) {
        const transaction = database.transaction([STORE_META], 'readwrite');
        transaction.objectStore(STORE_META).put(record);
        await committed(transaction);
    }
    async function createVaultKey(database, password) {
        const salt = cryptography.getSalt();
        const kek = await cryptography.deriveKek(password, salt, KDF_ITERATIONS);
        const { dataKey: created, wrapped } = await cryptography.createDataKey(kek);
        await writeMeta(database, {
            id: META_ID,
            kdf: { salt, iterations: KDF_ITERATIONS, hash: 'SHA-256' },
            wrapped,
        });
        return created;
    }
    return {
        initialize(config) {
            logger.debug(`[database] Initialized indexedDB with name ${config.dbName}.`);
            initialName = config.dbName;
        },
        async unlock(password, handlers) {
            const database = await open();
            if (password === '')
                return 'none';
            const meta = await readMeta(database);
            if (meta !== null) {
                try {
                    const kek = await cryptography.deriveKek(password, meta.kdf.salt, meta.kdf.iterations);
                    dataKey = await cryptography.unwrapDataKey(kek, meta.wrapped);
                }
                catch {
                    return 'invalid';
                }
            }
            else {
                if (await hasLegacyVault(database, cryptography)) {
                    if (!(await verifyLegacyPassword(database, cryptography, password)))
                        return 'invalid';
                }
                dataKey = await createVaultKey(database, password);
            }
            if ((await pendingMigrations(database, migrations)).length > 0) {
                handlers?.onMigrating?.();
                await drainMigrations(database, password, handlers);
            }
            return 'valid';
        },
        lock() {
            dataKey = null;
            messageIndexes.clear();
        },
        close() {
            dataKey = null;
            messageIndexes.clear();
            db?.close();
            db = null;
            openPromise = null;
        },
        isUnlocked() {
            return dataKey !== null;
        },
        async set(table, id, value) {
            const database = await open();
            const { iv, data } = await cryptography.encrypt(requireKey(), { id, value });
            const transaction = database.transaction([table], 'readwrite');
            transaction.objectStore(table).put({ id, iv, data });
            await committed(transaction);
            logger.debug(`[database] Data with key '${id}' encrypted and saved into table '${table}'.`);
        },
        async get(table, id) {
            const database = await open();
            const key = requireKey();
            const transaction = database.transaction([table], 'readonly');
            const row = await toPromise(transaction.objectStore(table).get(id));
            if (row === undefined) {
                logger.debug(`[database] Data with key '${id}' not found in table '${table}'.`);
                return null;
            }
            const decoded = await cryptography.decrypt(key, {
                iv: row.iv,
                data: row.data,
            });
            if (decoded.id !== id) {
                throw new Error(`[database] Row '${id}' in table '${table}' carries a mismatched key.`);
            }
            return decoded.value;
        },
        async getAll(table) {
            const database = await open();
            const key = requireKey();
            const rows = await readAll(database, table);
            const results = [];
            for (const row of rows) {
                results.push(await cryptography.decrypt(key, {
                    iv: row.iv,
                    data: row.data,
                }));
            }
            return results;
        },
        async delete(table, id) {
            const database = await open();
            const transaction = database.transaction([table], 'readwrite');
            transaction.objectStore(table).delete(id);
            await committed(transaction);
            logger.debug(`[database] Data with key '${id}' deleted from table '${table}'.`);
        },
        async clear(table) {
            const database = await open();
            const transaction = database.transaction([table], 'readwrite');
            transaction.objectStore(table).clear();
            await committed(transaction);
            logger.debug(`[database] All keys in table '${table}' cleared.`);
        },
        async getMessages(chatId) {
            const database = await open();
            const key = requireKey();
            const transaction = database.transaction([STORE_MESSAGES], 'readonly');
            const rows = await toPromise(transaction.objectStore(STORE_MESSAGES).getAll(chatRange(chatId)));
            const index = new Map();
            const messages = [];
            for (const row of rows) {
                try {
                    const message = await cryptography.decrypt(key, {
                        iv: row.iv,
                        data: row.data,
                    });
                    index.set(messageRowKey(message), row.n);
                    messages.push(message);
                }
                catch (error) {
                    logger.error(`[database] Skipping unreadable message row ${chatId}/${row.n}.`, error);
                }
            }
            messageIndexes.set(chatId, index);
            return messages;
        },
        async putMessages(chatId, upserts, deletes) {
            if (upserts.length === 0 && deletes.length === 0)
                return;
            const database = await open();
            const key = requireKey();
            let index = messageIndexes.get(chatId);
            if (index === undefined) {
                index = new Map();
                messageIndexes.set(chatId, index);
            }
            const prepared = [];
            for (const message of upserts) {
                if (message.sender === 'date')
                    continue;
                const { iv, data } = await cryptography.encrypt(key, message);
                prepared.push({ key: messageRowKey(message), iv, data });
            }
            const assigned = new Map();
            const removed = [];
            await new Promise((resolve, reject) => {
                const transaction = database.transaction([STORE_MESSAGES], 'readwrite');
                const store = transaction.objectStore(STORE_MESSAGES);
                const cursor = store.openCursor(chatRange(chatId), 'prev');
                cursor.onsuccess = () => {
                    const last = cursor.result;
                    let next = last === null ? 0 : last.key[1] + 1;
                    for (const item of prepared) {
                        let n = index.get(item.key) ?? assigned.get(item.key);
                        if (n === undefined) {
                            n = next++;
                            assigned.set(item.key, n);
                        }
                        store.put({ c: chatId, n, iv: item.iv, data: item.data });
                    }
                    for (const messageKey of deletes) {
                        const n = index.get(messageKey);
                        if (n === undefined)
                            continue;
                        store.delete([chatId, n]);
                        removed.push(messageKey);
                    }
                };
                transaction.oncomplete = () => resolve();
                transaction.onerror = () => reject(transaction.error);
                transaction.onabort = () => reject(transaction.error);
            });
            for (const [messageKey, n] of assigned) {
                index.set(messageKey, n);
            }
            for (const messageKey of removed) {
                index.delete(messageKey);
            }
        },
        async clearMessages(chatId) {
            const database = await open();
            const transaction = database.transaction([STORE_MESSAGES], 'readwrite');
            transaction.objectStore(STORE_MESSAGES).delete(chatRange(chatId));
            await committed(transaction);
            messageIndexes.delete(chatId);
        },
    };
}
